import React, { useState, useEffect } from "react";
import ExpandableText from "./ExpandableText";
import { useTranslation } from "react-i18next";
import {
  CalendarDays,
  Check,
  Fish,
  History,
  Leaf,
  MapPin,
  Share2,
  Trash2,
  Waves,
  X,
} from "lucide-react";
import "../styles/components/PlannedTrips.css";
import API_BASE, { withApiCredentials } from "../api";
import { trackProductEvent } from "../analytics/trackEvent";

function withTripApiCredentials(options = {}) {
  const token =
    typeof window !== "undefined" ? window.localStorage.getItem("token") : "";
  if (!token) {
    return withApiCredentials(options);
  }

  const headers = new Headers(options.headers || {});
  headers.set("Authorization", `Bearer ${token}`);
  return withApiCredentials({
    ...options,
    headers,
  });
}

export default function PlannedTrips({ 
  trips = [], 
  setSelectedTrip, 
  refreshTrips, 
  customTitle,      // <--- NEW PROP
  emptyMessage,      // <--- NEW PROP
  panel = "planned",
  onClose,
  closeLabel
}) {
  const { t } = useTranslation();

  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    tripId: null,
  });

  const [friends, setFriends] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTrip, setShareTrip] = useState(null);
  const [search, setSearch] = useState("");

  // ============================
  // Load friends + users map
  // ============================
  useEffect(() => {
    async function loadFriends() {
      try {
        const meRes = await fetch(
          `${API_BASE}/auth/me`,
          withTripApiCredentials()
        );
        if (!meRes.ok) return;
        const me = await meRes.json();
        setFriends(me.friends || []);

        const usersRes = await fetch(`${API_BASE}/auth/users`);
        if (!usersRes.ok) return;
        const users = await usersRes.json();

        const map = {};
        users.forEach((u) => {
          map[u.id] = u;
        });
        setUsersMap(map);
      } catch (err) {
        console.error("Failed to load friends/users", err);
      }
    }

    loadFriends();
  }, []);

  const formatTripRange = (trip) => {
    if (!trip.startDate) return null;

    const start = new Date(trip.startDate);
    const end = trip.endDate ? new Date(trip.endDate) : null;

    const fmt = (d) =>
      d.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });

    return end ? `${fmt(start)} -> ${fmt(end)}` : fmt(start);
  };

  const getPanelMeta = () => {
    if (panel === "current") {
      return {
        key: "current",
        title: t("trip_current", { defaultValue: "Current Trips" }),
        subtitle: t("trips_current_subtitle", {
          defaultValue: "Active trips you're currently on.",
        }),
        status: "Active",
        Icon: Fish,
      };
    }

    if (panel === "past") {
      return {
        key: "past",
        title: t("trip_past", { defaultValue: "Past Trips" }),
        subtitle: t("trips_past_subtitle", {
          defaultValue: "Trips you've completed and can relive.",
        }),
        status: "Past",
        Icon: History,
      };
    }

    return {
      key: "planned",
      title: t("planned_trips_title", { defaultValue: "Planned Trips" }),
      subtitle: t("trips_planned_subtitle", {
        defaultValue: "Upcoming trips you're getting ready for.",
      }),
      status: "Planned",
      Icon: CalendarDays,
    };
  };

  const buildMobileDetails = (trip, hasDates) => {
    const details = [];

    if (hasDates) {
      details.push({
        key: "dates",
        label: t("trip_dates", { defaultValue: "Dates" }),
        value: formatTripRange(trip),
        Icon: CalendarDays,
      });
    }

    if (trip.location) {
      details.push({
        key: "location",
        label: t("trip_location", { defaultValue: "Location" }),
        value: trip.location,
        Icon: MapPin,
      });
    }

    if (trip.species) {
      details.push({
        key: "species",
        label: t("trip_target_species", { defaultValue: "Target species" }),
        value: trip.species,
        Icon: Fish,
      });
    }

    if (trip.season) {
      details.push({
        key: "season",
        label: t("trip_season", { defaultValue: "Season" }),
        value: trip.season,
        Icon: Leaf,
      });
    }

    if (trip.fishingType) {
      details.push({
        key: "fishingType",
        label: t("trip_fishing_type", { defaultValue: "Fishing type" }),
        value: trip.fishingType,
        Icon: Waves,
      });
    }

    return details;
  };

  // ============================
  // DELETE TRIP
  // ============================
  const deleteTrip = async (tripId) => {
    try {
      await fetch(
        `${API_BASE}/trips/${tripId}`,
        withTripApiCredentials({ method: "DELETE" })
      );

      await refreshTrips();

      if (trips.length <= 1) {
        setSelectedTrip(null);
      }
    } catch (err) {
      console.error("Failed to delete trip:", err);
    }
  };

  // ============================
  // OPEN SHARE MODAL
  // ============================
  const handleShare = (trip) => {
    setShareTrip(trip);
    setSearch("");
    setShareOpen(true);
  };

  // ============================
  // SEND SHARE
  // ============================
  const sendShare = async (toUserId) => {
    if (!shareTrip) return;

    try {
      const meRes = await fetch(
        `${API_BASE}/auth/me`,
        withTripApiCredentials()
      );
      if (!meRes.ok) throw new Error("Failed to load user");
      const me = await meRes.json();

      const res = await fetch(
        `${API_BASE}/trips/${shareTrip.id}/share`,
        withTripApiCredentials({
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            toUserId,
            fromUserId: me.id,
            fromUsername: me.username,
          }),
        })
      );

      if (!res.ok) throw new Error("Share failed");

      trackProductEvent("trip_shared", {
        featureArea: "trips",
        metadata: { source: "planned_trip_share" },
      });
      alert(t("share_sent_success"));
      setShareOpen(false);
    } catch (err) {
      console.error("Failed to share trip:", err);
      alert(t("share_failed"));
    }
  };

  const panelMeta = getPanelMeta();
  const PanelIcon = panelMeta.Icon;

  return (
    <div className="planned-trips-container">
      <h2 className="planned-trips-title">
  {customTitle || t("planned_trips_title")}
</h2>

      <div className="planned-trips-mobile-intro">
        <span className="planned-trips-mobile-intro__icon" aria-hidden="true">
          <PanelIcon size={19} strokeWidth={2.35} />
        </span>
        <div className="planned-trips-mobile-intro__copy">
          <h3>{panelMeta.title}</h3>
          <p>{panelMeta.subtitle}</p>
        </div>
        {onClose ? (
          <button
            type="button"
            className="planned-trips-mobile-intro__close"
            aria-label={closeLabel}
            onClick={onClose}
          >
            <X size={18} strokeWidth={2.5} aria-hidden="true" />
          </button>
        ) : null}
      </div>

      {trips.length === 0 ? (
        <p className="planned-trips-empty">
  {emptyMessage || t("planned_trips_empty")}
</p>
      ) : (
        trips.map((trip) => {
          const hasDates = !!trip.startDate;
          const showSeasonLine = !hasDates && trip.season;
          const mobileDetails = buildMobileDetails(trip, hasDates);

          return (
            <div key={trip.id} className="planned-trip-card">
              <div className="planned-trip-info">
                <div className="planned-trip-mobile-card-head">
                  <h3 className="planned-trip-mobile-title">
                    {trip.name || t("trip_untitled", { defaultValue: "Untitled trip" })}
                  </h3>
                  <span
                    className={`planned-trip-status-pill planned-trip-status-pill--${panelMeta.key}`}
                  >
                    {panelMeta.status}
                  </span>
                </div>

                <h3 className="planned-trip-name">{trip.name}</h3>

                {trip.proposedByUsername && (
                  <p className="planned-trip-proposed">
                    {t("trip_proposed_by")} {trip.proposedByUsername}
                  </p>
                )}

                {hasDates && (
                  <p className="planned-trip-info-line">
                    <strong>{t("trip_dates")}:</strong>{" "}
                    {formatTripRange(trip)}
                  </p>
                )}

                {trip.location && (
                  <p className="planned-trip-info-line">
                    <strong>{t("trip_location")}:</strong> {trip.location}
                  </p>
                )}

                {trip.species && (
                  <p className="planned-trip-info-line">
                    <strong>{t("trip_target_species")}:</strong>{" "}
                    {trip.species}
                    {trip.season && (
                      <span className="planned-trip-season-inline">
                        {" "}({t("trip_season")}: {trip.season})
                      </span>
                    )}
                  </p>
                )}

                {showSeasonLine && (
                  <p className="planned-trip-info-line">
                    <strong>{t("trip_season")}:</strong> {trip.season}
                  </p>
                )}

                {trip.fishingType && (
                  <p className="planned-trip-info-line">
                    <strong>{t("trip_fishing_type")}:</strong>{" "}
                    {trip.fishingType}
                  </p>
                )}

                {trip.notes && (
                  <div className="planned-trip-info-line">
                    <strong>{t("trip_notes")}:</strong>{" "}
                    <ExpandableText text={trip.notes} maxChars={120} />
                  </div>
                )}

                {mobileDetails.length > 0 && (
                  <div className="planned-trip-mobile-details">
                    {mobileDetails.map(({ key, label, value, Icon }) => (
                      <div key={key} className="planned-trip-mobile-detail">
                        <span
                          className="planned-trip-mobile-detail__icon"
                          aria-hidden="true"
                        >
                          <Icon size={15} strokeWidth={2.25} />
                        </span>
                        <span className="planned-trip-mobile-detail__copy">
                          <span className="planned-trip-mobile-detail__label">
                            {label}
                          </span>
                          <span className="planned-trip-mobile-detail__value">
                            {value}
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* BUTTON COLUMN */}
              <div className="planned-trip-button-column">
                <button
                  onClick={() =>
                    setConfirmDelete({ open: true, tripId: trip.id })
                  }
                  className="planned-trip-delete-btn"
                >
                  <Trash2 size={15} strokeWidth={2.25} aria-hidden="true" />
                  {t("delete")}
                </button>

                <button
                  onClick={() => handleShare(trip)}
                  className="planned-trip-share-btn"
                >
                  <Share2 size={15} strokeWidth={2.25} aria-hidden="true" />
                  {t("share")}
                </button>

                <button
                  onClick={() => setSelectedTrip(trip)}
                  className="planned-trip-select-btn"
                >
                  <Check size={15} strokeWidth={2.4} aria-hidden="true" />
                  {t("select")}
                </button>
              </div>
            </div>
          );
        })
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {confirmDelete.open && (
        <div className="planned-trip-modal-overlay">
          <div className="planned-trip-modal-box">
            <h3 className="planned-trip-modal-title">
              {t("delete_trip_confirm_title")}
            </h3>

            <p className="planned-trip-modal-text">
              {t("delete_trip_confirm_text")}
            </p>

            <div className="planned-trip-modal-actions">
              <button
                className="planned-trip-cancel-btn"
                onClick={() =>
                  setConfirmDelete({ open: false, tripId: null })
                }
              >
                {t("cancel")}
              </button>

              <button
                className="planned-trip-delete-confirm-btn"
                onClick={() => {
                  deleteTrip(confirmDelete.tripId);
                  setConfirmDelete({ open: false, tripId: null });
                }}
              >
                {t("confirm_delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SHARE FRIEND PICKER MODAL */}
      {shareOpen && (
        <div className="planned-trip-modal-overlay">
          <div className="planned-trip-modal-box">
            <h3 className="planned-trip-modal-title">
              {t("share_with_friend")}
            </h3>

            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("share_search_friend")}
              className="planned-trip-share-input"
            />

            <div className="planned-trip-share-list">
              {friends
                .map((id) => usersMap[id])
                .filter(Boolean)
                .filter((u) =>
                  u.username
                    .toLowerCase()
                    .includes(search.toLowerCase())
                )
                .map((u) => (
                  <div
                    key={u.id}
                    className="planned-trip-share-item"
                    onClick={() => sendShare(u.id)}
                  >
                    <img
                      src={
                        u.avatar
                          ? `${API_BASE}${u.avatar}`
                          : "https://cdn-icons-png.flaticon.com/512/847/847969.png"
                      }
                      alt={t("profile_picture")}
                      className="planned-trip-share-avatar"
                    />
                    <span>{u.username}</span>
                  </div>
                ))}

              {friends.length === 0 && (
                <p>{t("no_friends")}</p>
              )}
            </div>

            <div className="planned-trip-modal-actions">
              <button
                className="planned-trip-cancel-btn"
                onClick={() => setShareOpen(false)}
              >
                {t("cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
